import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import useEmblaCarousel from "embla-carousel-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Logo } from "@/components/Logo";
import { RichTextView } from "@/components/cms/RichText";
import {
  HERO_LOGO_PX, resolveLogoPx, HERO_HEIGHT_PAD, HERO_HEIGHT_MINH, HERO_BUFFER_TOP, HERO_BUFFER_BOTTOM, objectPosition,
} from "@/lib/heroPresets";
import {
  Phone,
  MessageCircle,
  CalendarHeart,
  Home,
  ShieldCheck,
  HeartHandshake,
  Building2,
  PackageOpen,
  Wrench,
  Check,
  ArrowRight,
  Quote,
  Sparkles,
  User,
  Users,
  Heart,
  UserRound,
  Briefcase,
  Star,
  Play,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const telHref = (phone) => `tel:${(phone || "").replace(/[^0-9+]/g, "")}`;
const CONTAINER = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";

const SERVICE_ICONS = {
  sell_home: Home,
  home_safety: ShieldCheck,
  move_into_my_home: HeartHandshake,
  senior_placement: Building2,
  downsizing: PackageOpen,
  home_repairs: Wrench,
};

const PERSONA_ICONS = {
  daughter: UserRound,
  son: User,
  spouse: Heart,
  caregiver: HeartHandshake,
  senior: Users,
  professional: Briefcase,
};

const LANE_SLUG = {
  sell_home: "selling-the-home",
  home_safety: "keep-mama-home",
  move_into_my_home: "move-mama-into-my-home",
  senior_placement: "senior-living-placement",
  downsizing: "downsizing-estate-support",
  home_repairs: "keep-mama-home",
};

const SectionHeading = ({ eyebrow, title, subtitle, dark, center = true }) => (
  <div className={`${center ? "text-center mx-auto max-w-3xl" : "max-w-3xl"}`}>
    {eyebrow && (
      <p className={`text-[14px] font-semibold uppercase tracking-[0.14em] ${dark ? "text-[#DEE029]" : "text-[#9C1E22]"}`}>
        {eyebrow}
      </p>
    )}
    <h2 className={`mt-3 text-3xl sm:text-4xl font-display font-bold tracking-tight ${dark ? "text-white" : "text-[#1C4358]"}`}>
      {title}
    </h2>
    {subtitle && (
      <p className={`mt-4 text-lg leading-relaxed ${dark ? "text-white/80" : "text-[#4B5563]"}`}>{subtitle}</p>
    )}
  </div>
);

/* ----------------------------------------------------------------- Hero */
export const TrustBadge = ({ className = "", lane = null, franchise = null }) => {
  const [s, setS] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fr = franchise || params.get("franchise");
    const qs = new URLSearchParams();
    if (fr) qs.set("franchise", fr);
    if (lane) qs.set("lane", lane);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    publicApi
      .get(`/public/testimonials/rating-summary${suffix}`)
      .then(({ data }) => setS(data))
      .catch(() => setS(null));
  }, [lane, franchise]);
  if (!s) return null;
  return (
    <div data-testid="trust-badge"
      className={`inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-2 text-white ${className}`}>
      {s.enough ? (
        <>
          <span className="flex items-center gap-0.5" data-testid="trust-badge-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} size={15} className={n <= Math.round(s.average) ? "fill-[#E0A526] text-[#E0A526]" : "text-white/40"} />
            ))}
          </span>
          <span className="text-[14px] font-semibold" data-testid="trust-badge-text">
            {s.average.toFixed(1)} from {s.count} {s.count === 1 ? "family" : "families"}
          </span>
        </>
      ) : (
        <span className="text-[14px] font-medium text-white/90" data-testid="trust-badge-soft">
          <Heart size={14} className="inline mr-1.5 -mt-0.5 text-[#E0A526]" />
          Families are beginning to share their MovingMyMama experience.
        </span>
      )}
    </div>
  );
};

const OVERLAY_CLASS = {
  side: "mmm-hero-overlay-side",
  bottom: "bg-gradient-to-t from-black/75 via-black/35 to-black/10",
  dark: "bg-black/55",
  none: "bg-black/20",
};

// Parse a video URL into an embeddable form (YouTube / Vimeo / direct .mp4)
const videoEmbed = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { type: "iframe", src: `https://www.youtube-nocookie.com/embed/${yt[1]}?rel=0` };
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { type: "iframe", src: `https://player.vimeo.com/video/${vm[1]}` };
  if (/\.mp4(\?|$)/i.test(url)) return { type: "mp4", src: url };
  return { type: "iframe", src: url };
};

// Enabled, ordered hero slides. Returns [] when the site uses the classic single hero.
const getHeroSlides = (hero = {}) => {
  const slides = Array.isArray(hero.slides) ? hero.slides : [];
  return slides
    .filter((s) => s && s.enabled !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const HeroVideoModal = ({ open, onClose, url, title }) => {
  const embed = videoEmbed(url);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black border-0" data-testid="hero-video-modal">
        <DialogTitle className="sr-only">{title || "Video"}</DialogTitle>
        <DialogDescription className="sr-only">Play video</DialogDescription>
        <div className="relative w-full" style={{ aspectRatio: "16 / 9" }}>
          {embed?.type === "mp4" ? (
            <video src={embed.src} controls autoPlay playsInline className="h-full w-full" data-testid="hero-video-el" />
          ) : embed ? (
            <iframe
              src={`${embed.src}${embed.src.includes("?") ? "&" : "?"}autoplay=1`}
              title={title || "Video"}
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="hero-video-iframe"
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const HeroSlideContent = ({ slide, content, onChat, onWelcome, franchise }) => {
  const h = content?.hero || {};
  const wv = content?.welcome_video || {};
  const logoPx = resolveLogoPx(h.logo_size, HERO_LOGO_PX);
  const bufTop = HERO_BUFFER_TOP[h.pad_top] || HERO_BUFFER_TOP.normal;
  const bufBottom = HERO_BUFFER_BOTTOM[h.pad_bottom] || HERO_BUFFER_BOTTOM.normal;
  const eyebrow = slide.eyebrow ?? h.eyebrow;
  const headline = slide.headline ?? h.headline;
  const subheadline = slide.subheadline ?? h.subheadline;
  const ctaLabel = slide.cta_label || h.cta_primary?.label || "Book a Free Consultation";
  const ctaHref = slide.cta_url || h.cta_primary?.href || "/book";
  const showCall = slide.show_call !== false;
  const showChat = slide.show_chat !== false;
  const showWelcome = !!(wv.enabled && wv.video_url && onWelcome);
  return (
    <div className={`max-w-2xl mmm-fade-up ${bufTop} ${bufBottom}`}>
      <div className="mb-6 flex items-center" data-testid="hero-logo">
        <Logo size={logoPx} />
      </div>
      {eyebrow && (
        <p className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-[14px] font-semibold text-white">
          <Sparkles size={15} className="text-[#DEE029]" /> {eyebrow}
        </p>
      )}
      <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight text-white leading-[1.08]">
        {headline}
      </h1>
      {subheadline && <p className="mt-6 text-lg sm:text-xl text-white/90 leading-relaxed max-w-xl">{subheadline}</p>}
      <TrustBadge className="mt-6" franchise={franchise} />
      <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-3">
        <Link
          to={ctaHref}
          data-testid="hero-book-consult-btn"
          className="mmm-tap inline-flex items-center justify-center gap-2 px-7 h-14 rounded-full text-[17px] font-semibold text-white bg-[#9C1E22] hover:bg-[#861A1D] transition-colors shadow-lg shadow-black/20"
        >
          <CalendarHeart size={20} /> {ctaLabel}
        </Link>
        {showCall && (
          <a
            href={telHref(h.cta_call?.phone || content?.phone)}
            data-testid="hero-call-btn"
            className="mmm-tap inline-flex items-center justify-center gap-2 px-6 h-14 rounded-full text-[17px] font-semibold text-[#1C4358] bg-white hover:bg-white/90 transition-colors"
          >
            <Phone size={19} /> {h.cta_call?.label || `Call ${content?.phone}`}
          </a>
        )}
        {showChat && (
          <button
            type="button"
            onClick={() => (onChat ? onChat({ cta_source: "Homepage Hero Chat" }) : (window.location.href = "/contact?source=live-chat"))}
            data-testid="hero-chat-btn"
            className="mmm-tap inline-flex items-center justify-center gap-2 px-6 h-14 rounded-full text-[17px] font-semibold text-white border-2 border-white/70 hover:bg-white/10 transition-colors"
          >
            <MessageCircle size={19} /> {h.cta_chat?.label || "Start Live Chat"}
          </button>
        )}
        {showWelcome && (
          <button
            type="button"
            onClick={onWelcome}
            data-testid="hero-welcome-video-btn"
            className="mmm-tap inline-flex items-center justify-center gap-2 px-6 h-14 rounded-full text-[16px] font-semibold text-white bg-white/15 hover:bg-white/25 backdrop-blur border border-white/40 transition-colors"
          >
            <Play size={18} className="fill-current" /> {wv.button_label || "Watch Welcome Video"}
          </button>
        )}
      </div>
      <p className="mt-6 text-[15px] text-white/80">Free · No pressure · One caring guide from start to finish</p>
    </div>
  );
};

const HeroSlide = ({ slide, content, onChat, onWelcome, franchise, onPlay }) => {
  const isVideo = slide.type === "video";
  const bg = isVideo ? slide.poster_url || slide.image_url : slide.image_url;
  const overlay = OVERLAY_CLASS[slide.overlay] || OVERLAY_CLASS.side;
  const h = content?.hero || {};
  const heightPad = HERO_HEIGHT_PAD[h.height] || HERO_HEIGHT_PAD.standard;
  const minH = HERO_HEIGHT_MINH[h.height] || HERO_HEIGHT_MINH.standard;
  const objPos = objectPosition(h.focal_x, h.focal_y);
  return (
    <section className={`relative isolate overflow-hidden flex items-center ${minH}`} data-testid="hero-section">
      {bg ? (
        <img src={bg} alt={slide.alt || "MovingMyMama — helping families move forward"} className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: objPos }} />
      ) : (
        <div className="absolute inset-0 bg-[#1C4358]" />
      )}
      <div className={`absolute inset-0 ${overlay}`} />
      {isVideo && slide.video_url && (
        <button
          type="button"
          onClick={onPlay}
          data-testid="hero-video-play"
          aria-label={`Play video${slide.caption ? `: ${slide.caption}` : ""}`}
          className="absolute inset-0 z-20 flex items-center justify-center group"
        >
          <span className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-white/90 text-[#9C1E22] shadow-xl transition-transform group-hover:scale-110">
            <Play size={30} className="ml-1 fill-current" />
          </span>
        </button>
      )}
      <div className={`${CONTAINER} relative w-full ${heightPad}`}>
        <HeroSlideContent slide={slide} content={content} onChat={onChat} onWelcome={onWelcome} franchise={franchise} />
      </div>
    </section>
  );
};

export const Hero = ({ content, onChat, onWelcome, franchise = null }) => {
  const h = content?.hero || {};
  const slides = getHeroSlides(h);

  // Classic single hero (backward compatible) when no carousel slides configured.
  const singleSlide = {
    type: "image",
    image_url: h.image_url,
    alt: "A peaceful road at sunset — the journey forward for your family",
    overlay: "side",
  };
  const list = slides.length ? slides : [singleSlide];
  const multi = list.length > 1;

  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "start", active: multi });
  const [selected, setSelected] = useState(0);
  const [video, setVideo] = useState(null); // { url, title } | null

  const onSelect = useCallback(() => {
    if (embla) setSelected(embla.selectedScrollSnap());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    embla.on("select", onSelect);
    onSelect();
    return () => embla.off("select", onSelect);
  }, [embla, onSelect]);

  // Auto-advance (paused while a video modal is open, or reduced motion)
  useEffect(() => {
    if (!embla || !multi || reduced || video) return;
    const id = setInterval(() => embla.scrollNext(), 7000);
    return () => clearInterval(id);
  }, [embla, multi, reduced, video]);

  if (!multi) {
    return (
      <>
        <HeroSlide slide={list[0]} content={content} onChat={onChat} onWelcome={onWelcome} franchise={franchise} onPlay={() => setVideo({ url: list[0].video_url, title: list[0].caption })} />
        <HeroVideoModal open={!!video} onClose={() => setVideo(null)} url={video?.url} title={video?.title} />
      </>
    );
  }

  return (
    <div className="relative" data-testid="hero-carousel" aria-roledescription="carousel">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {list.map((slide, i) => (
            <div className="min-w-0 flex-[0_0_100%]" key={slide.id || i} aria-hidden={selected !== i}>
              <HeroSlide slide={slide} content={content} onChat={onChat} onWelcome={onWelcome} franchise={franchise} onPlay={() => setVideo({ url: slide.video_url, title: slide.caption })} />
            </div>
          ))}
        </div>
      </div>

      <button type="button" onClick={() => embla && embla.scrollPrev()} data-testid="hero-prev" aria-label="Previous slide"
        className="mmm-tap absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-30 h-11 w-11 inline-flex items-center justify-center rounded-full bg-black/30 hover:bg-black/50 text-white backdrop-blur transition-colors">
        <ChevronLeft size={22} />
      </button>
      <button type="button" onClick={() => embla && embla.scrollNext()} data-testid="hero-next" aria-label="Next slide"
        className="mmm-tap absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-30 h-11 w-11 inline-flex items-center justify-center rounded-full bg-black/30 hover:bg-black/50 text-white backdrop-blur transition-colors">
        <ChevronRight size={22} />
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2" role="tablist" aria-label="Choose slide">
        {list.map((_, i) => (
          <button key={i} type="button" role="tab" aria-current={selected === i} aria-label={`Go to slide ${i + 1}`}
            data-testid={`hero-dot-${i}`} onClick={() => embla && embla.scrollTo(i)} className="mmm-hero-dot" />
        ))}
      </div>

      <HeroVideoModal open={!!video} onClose={() => setVideo(null)} url={video?.url} title={video?.title} />
    </div>
  );
};

/* --------------------------------------------------------- Persona panel */
export const PersonaPanel = ({ content }) => {
  const p = content?.persona || {};
  return (
    <section id="who-you-are" className="py-16 sm:py-24 bg-[#FBF6EC]" data-testid="persona-section">
      <div className={CONTAINER}>
        <SectionHeading eyebrow="Start here" title={p.title} subtitle={p.subtitle} />
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {(p.options || []).map((opt) => {
            const Icon = PERSONA_ICONS[opt.key] || User;
            return (
              <Link
                key={opt.key}
                to={opt.href}
                data-testid={`persona-${opt.key}`}
                className="mmm-tile mmm-tap group flex flex-col items-center justify-center gap-2.5 rounded-[0.9rem] bg-white border border-[#1C4358]/10 px-3 py-7 text-center shadow-sm shadow-[#1C4358]/10 hover:border-[#9BB9CE]"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#9BB9CE]/25 text-[#1C4358] group-hover:bg-[#1C4358] group-hover:text-white transition-colors">
                  <Icon size={22} />
                </span>
                <span className="text-[16px] font-semibold text-[#1C4358]">{opt.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* --------------------------------------------------------- Service lanes */
export const ServiceLanes = ({ content }) => {
  const s = content?.services || {};
  return (
    <section id="services" className="py-16 sm:py-24 scroll-mt-24" data-testid="services-section">
      <div className={CONTAINER}>
        <SectionHeading eyebrow="How we help" title={s.title} subtitle={s.subtitle} />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {(s.items || []).map((item) => {
            const Icon = SERVICE_ICONS[item.key] || Home;
            return (
              <Link
                key={item.key}
                to={`/services/${LANE_SLUG[item.key] || "start-here"}`}
                data-testid={`service-card-${item.key}`}
                className="mmm-tile group flex flex-col overflow-hidden rounded-[0.9rem] bg-white border border-[#1C4358]/10 shadow-sm shadow-[#1C4358]/10"
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={item.image_url} alt={item.title} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1C4358]/45 to-transparent" />
                  <span className="absolute bottom-3 left-3 inline-flex items-center justify-center h-11 w-11 rounded-xl bg-white text-[#9C1E22] shadow">
                    <Icon size={22} />
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="text-xl font-display font-semibold text-[#1C4358]">{item.title}</h3>
                  <p className="mt-2 text-[16px] text-[#4B5563] leading-relaxed flex-1">{item.description}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[15px] font-semibold text-[#9C1E22] group-hover:gap-2.5 transition-all">
                    Learn more <ArrowRight size={16} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------- Process */
export const ProcessSteps = ({ content }) => {
  const p = content?.process || {};
  return (
    <section id="how-it-works" className="py-16 sm:py-24 bg-[#1C4358] mmm-grain scroll-mt-24" data-testid="process-section">
      <div className={CONTAINER}>
        <SectionHeading eyebrow="Simple & calm" title={p.title} subtitle={p.subtitle} dark />
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {(p.steps || []).map((step) => (
            <div key={step.n} data-testid={`process-step-${step.n}`} className="relative rounded-[0.9rem] bg-white/5 border border-white/10 p-8">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#DEE029] text-[#1C4358] text-2xl font-display font-bold">
                {step.n}
              </div>
              <h3 className="mt-5 text-xl font-display font-semibold text-white">{step.title}</h3>
              <p className="mt-2 text-[16px] text-white/80 leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <Link
            to="/book"
            data-testid="process-cta"
            className="mmm-tap inline-flex items-center justify-center gap-2 px-7 h-14 rounded-full text-[17px] font-semibold text-[#1C4358] bg-[#DEE029] hover:bg-[#c9cb17] transition-colors"
          >
            Book your free consultation <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ---------------------------------------------------------------- Trust */
export const TrustSection = ({ content }) => {
  const t = content?.trust || {};
  return (
    <section className="py-16 sm:py-24" data-testid="trust-section">
      <div className={CONTAINER}>
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <div className="relative overflow-hidden rounded-[1.2rem] shadow-md shadow-[#1C4358]/10">
            <img src={t.image_url} alt={t.image_alt || "A family finding a clear path together"} className="h-full w-full object-cover" />
          </div>
          <div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-[#1C4358] leading-tight">
              {t.title}
            </h2>
            {t.body_html ? (
              <RichTextView html={t.body_html} className="mt-5 text-lg text-[#4B5563] leading-relaxed" data-testid="trust-body" />
            ) : (
              <p className="mt-5 text-lg text-[#4B5563] leading-relaxed">{t.body}</p>
            )}
            <ul className="mt-7 space-y-3.5">
              {(t.points || []).map((pt, i) => (
                <li key={i} data-testid={`trust-point-${i}`} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#2F7D5A]/12 text-[#2F7D5A]">
                    <Check size={16} />
                  </span>
                  <span className="text-[17px] text-[#252221]">{pt}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------- Trilogy */
export const TrilogyPreview = ({ content }) => {
  const t = content?.trilogy || {};
  return (
    <section className="py-16 sm:py-24 bg-[#C4C89B]/25" data-testid="trilogy-section">
      <div className={CONTAINER}>
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-[14px] font-semibold uppercase tracking-[0.14em] text-[#9C1E22]">Beyond the move</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-display font-bold tracking-tight text-[#1C4358]">{t.title}</h2>
            {t.body_html ? (
              <RichTextView html={t.body_html} className="mt-5 text-lg text-[#4B5563] leading-relaxed" data-testid="trilogy-body" />
            ) : (
              <p className="mt-5 text-lg text-[#4B5563] leading-relaxed">{t.body}</p>
            )}
            <Link
              to={t.cta?.href || "/trilogy-senior-wellness"}
              data-testid="trilogy-cta"
              className="mmm-tap mt-7 inline-flex items-center gap-2 px-6 h-14 rounded-full text-[16px] font-semibold text-white bg-[#1C4358] hover:bg-[#163645] transition-colors"
            >
              {t.cta?.label || "Explore Trilogy Senior Wellness"} <ArrowRight size={18} />
            </Link>
          </div>
          <div className="relative overflow-hidden rounded-[1.2rem] shadow-md shadow-[#1C4358]/10 order-first lg:order-last">
            <img src={t.image_url} alt="Seniors staying active and well" className="h-full w-full object-cover" />
          </div>
        </div>
      </div>
    </section>
  );
};

/* --------------------------------------------------------- Testimonials */
export const Testimonials = ({ content }) => {
  const t = content?.testimonials || {};
  return (
    <section className="py-16 sm:py-24" data-testid="testimonials-section">
      <div className={CONTAINER}>
        <SectionHeading eyebrow="Families we've walked with" title={t.title} subtitle={t.subtitle} />
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {(t.items || []).map((item, i) => (
            <figure key={i} data-testid={`testimonial-${i}`} className="flex flex-col rounded-[0.9rem] bg-[#FBF6EC] border border-[#1C4358]/10 p-7">
              <Quote size={30} className="text-[#9BB9CE]" />
              <blockquote className="mt-4 text-[17px] text-[#252221] leading-relaxed flex-1">"{item.quote}"</blockquote>
              <figcaption className="mt-5">
                <div className="font-display font-semibold text-[#1C4358]">{item.author}</div>
                <div className="text-[14px] text-[#4B5563]">{item.location}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

/* --------------------------------------------------- What Families Say (dynamic) */
const laneTag = (l) => (l ? String(l).replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : null);

export const FamilyTestimonials = ({ content, onChat, franchise = null }) => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    const fr = franchise || new URLSearchParams(window.location.search).get("franchise");
    publicApi
      .get(`/public/testimonials${fr ? `?franchise=${encodeURIComponent(fr)}` : ""}`)
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]));
  }, [franchise]);

  if (items === null) return null; // loading — avoid layout shift
  const fallback = (content?.testimonials?.items || []).map((x, i) => ({
    id: `curated-${i}`, text: x.quote, display_name: x.author, location: x.location, rating: null, service_lane: null,
  }));
  const list = items.length ? items : fallback;
  if (!list.length) return null;

  const t = content?.testimonials || {};
  return (
    <section className="py-16 sm:py-24 bg-[#FBF6EC]" data-testid="family-testimonials-section">
      <div className={CONTAINER}>
        <SectionHeading eyebrow="Families we've walked with" title={t.title || "What Families Say"}
          subtitle={t.subtitle || "Real words from families we've helped through a senior transition."} />
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {list.map((item) => {
            const loc = item.location || [item.city, item.state].filter(Boolean).join(", ");
            return (
              <figure key={item.id} data-testid={`testimonial-card-${item.id}`}
                className="flex flex-col rounded-[0.9rem] bg-white border border-[#1C4358]/10 p-7 shadow-sm shadow-[#1C4358]/10">
                <div className="flex items-center justify-between">
                  <Quote size={30} className="text-[#9BB9CE]" />
                  {item.rating ? (
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} size={15} className={n <= item.rating ? "fill-[#E0A526] text-[#E0A526]" : "text-[#D6E0E7]"} />
                      ))}
                    </div>
                  ) : null}
                </div>
                <blockquote className="mt-4 text-[17px] text-[#252221] leading-relaxed flex-1">"{item.text}"</blockquote>
                <figcaption className="mt-5">
                  <div className="font-display font-semibold text-[#1C4358]">{item.display_name}</div>
                  <div className="text-[14px] text-[#4B5563]">
                    {loc}{loc && item.service_lane ? " · " : ""}{laneTag(item.service_lane)}
                  </div>
                </figcaption>
              </figure>
            );
          })}
        </div>

        <div className="mt-14 text-center">
          <p className="text-2xl font-display font-semibold text-[#1C4358]">Need help with your family's next step?</p>
          <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/book?from=testimonials" data-testid="testimonials-cta-book"
              className="mmm-tap inline-flex items-center justify-center gap-2 px-8 h-14 rounded-full text-[17px] font-semibold text-white bg-[#9C1E22] hover:bg-[#861A1D] transition-colors">
              <CalendarHeart size={20} /> Book Free Consultation
            </Link>
            <button type="button" data-testid="testimonials-cta-chat"
              onClick={() => (onChat ? onChat({ cta_source: "Testimonials Chat" }) : (window.location.href = "/contact?source=live-chat"))}
              className="mmm-tap inline-flex items-center justify-center gap-2 px-7 h-14 rounded-full text-[17px] font-semibold text-[#1C4358] bg-white border border-[#1C4358]/20 hover:bg-[#F4F8FB] transition-colors">
              <MessageCircle size={19} /> Start Live Chat
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

/* --------------------------------------------------------- Resources */
export const ResourcesTeaser = ({ content }) => {
  const r = content?.resources || {};
  return (
    <section id="resources" className="py-16 sm:py-24 bg-[#FBF6EC] scroll-mt-24" data-testid="resources-section">
      <div className={CONTAINER}>
        <SectionHeading eyebrow="Resources & Q&A" title={r.title} subtitle={r.subtitle} />
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {(r.items || []).map((item, i) => (
            <Link
              key={i}
              to={item.href && item.href.startsWith("/resources") ? item.href : "/resources/faq"}
              data-testid={`resource-${i}`}
              className="mmm-tile group flex flex-col rounded-[0.9rem] bg-white border border-[#1C4358]/10 p-7 shadow-sm shadow-[#1C4358]/10 hover:border-[#1C4358]/30 transition-colors"
            >
              <h3 className="text-xl font-display font-semibold text-[#1C4358]">{item.title}</h3>
              <p className="mt-3 text-[16px] text-[#4B5563] leading-relaxed flex-1">{item.excerpt}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-[15px] font-semibold text-[#9C1E22]">
                Read the answer <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link
            to="/resources"
            data-testid="resources-teaser-see-all"
            className="mmm-tap inline-flex items-center gap-2 px-7 h-13 rounded-full text-[16px] font-semibold text-white bg-[#1C4358] hover:bg-[#163545] transition-colors"
          >
            Explore all family resources <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------- Final CTA */
export const FinalCTA = ({ content }) => {
  const c = content?.final_cta || {};
  return (
    <section id="consultation" className="py-16 sm:py-24 scroll-mt-24" data-testid="final-cta-section">
      <div className={CONTAINER}>
        <div className="relative overflow-hidden rounded-[1.4rem] bg-[#1C4358] mmm-grain px-6 py-14 sm:px-14 sm:py-20 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight text-white max-w-3xl mx-auto leading-tight">
            {c.title}
          </h2>
          <p className="mt-5 text-lg text-white/85 max-w-2xl mx-auto leading-relaxed">{c.subtitle}</p>
          <div className="mt-9 flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to="/book?from=final_cta"
              data-testid="final-book-btn"
              className="mmm-tap inline-flex items-center justify-center gap-2 px-8 h-14 rounded-full text-[17px] font-semibold text-white bg-[#9C1E22] hover:bg-[#861A1D] transition-colors"
            >
              <CalendarHeart size={20} /> {c.cta_primary?.label || "Book a Free 1-Hour Consultation"}
            </Link>
            <a
              href={telHref(c.phone || content?.phone)}
              data-testid="final-call-btn"
              className="mmm-tap inline-flex items-center justify-center gap-2 px-7 h-14 rounded-full text-[17px] font-semibold text-[#1C4358] bg-white hover:bg-white/90 transition-colors"
            >
              <Phone size={19} /> Call {c.phone || content?.phone}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------ Homepage FAQ */
export const HomeFaq = ({ content, onChat }) => {
  const r = content?.faq || {};
  const [items, setItems] = useState(null);

  useEffect(() => {
    publicApi
      .get("/public/faq", { params: { homepage: 1 } })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]));
  }, []);

  // FAQPage JSON-LD for the visible homepage questions only.
  useEffect(() => {
    if (!items || !items.length) return undefined;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-mmm-jsonld-home-faq", "1");
    s.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((it) => ({
        "@type": "Question",
        name: it.question,
        acceptedAnswer: { "@type": "Answer", text: it.answer },
      })),
    });
    document.head.appendChild(s);
    return () => { document.querySelectorAll("script[data-mmm-jsonld-home-faq]").forEach((n) => n.remove()); };
  }, [items]);

  if (items === null || !items.length) return null;

  return (
    <section id="faq" className="py-16 sm:py-24 scroll-mt-24" data-testid="home-faq-section">
      <div className={CONTAINER}>
        <SectionHeading
          eyebrow="Questions families ask"
          title={r.title || "You've got questions. We've got warm, honest answers."}
          subtitle={r.subtitle || "Real answers to the things families ask us most — no jargon, no pressure."}
        />
        <div className="mx-auto mt-10 max-w-3xl">
          <Accordion type="single" collapsible className="space-y-3" data-testid="home-faq-accordion">
            {items.map((it) => (
              <AccordionItem
                key={it.id}
                value={it.slug || it.id}
                data-testid={`home-faq-item-${it.slug || it.id}`}
                className="border border-[#1C4358]/12 rounded-xl px-5 bg-white shadow-sm shadow-[#1C4358]/5"
              >
                <AccordionTrigger className="text-left text-[17px] font-display font-semibold text-[#1C4358] hover:no-underline py-5">
                  {it.question}
                </AccordionTrigger>
                <AccordionContent className="text-[16px] text-[#4B5563] leading-relaxed pb-5">
                  {it.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/resources/faq"
            data-testid="home-faq-view-more"
            className="mmm-tap inline-flex items-center gap-2 px-7 h-13 rounded-full text-[16px] font-semibold text-white bg-[#1C4358] hover:bg-[#163545] transition-colors"
          >
            View more FAQs <ArrowRight size={18} />
          </Link>
          <button
            type="button"
            onClick={() => (onChat ? onChat({ cta_source: "Homepage FAQ Chat" }) : (window.location.href = "/contact?source=live-chat"))}
            data-testid="home-faq-chat"
            className="mmm-tap inline-flex items-center gap-2 px-6 h-13 rounded-full text-[16px] font-semibold text-[#1C4358] border border-[#1C4358]/25 hover:bg-[#1C4358]/6 transition-colors"
          >
            <MessageCircle size={18} /> Ask us anything
          </button>
        </div>
      </div>
    </section>
  );
};
